import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import axios from 'axios';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import './App.css';
import './Utils.css';

import * as constants from './constants';
import {
    MobileBookingWidget,
    DesktopBookingWidget,
    DesktopHorizontalBookingWidget,
    // MultiPropertyBookingWidget,
} from './components/bookingWidget';
import { MultipropertyCarouselWidget } from './components/multipropertyCarouselWidget';
import {
    displayNotifications,
    checkWidgetIntervalsToDelete,
    saveWidgetStateToLS,
    isMobileDeviceRequest,
} from './lib';

import {
    getMiniBookingWidgetData,
    hide,
    minimize,
    resetNotificationsAvailable,
    setPrice,
    toggle,
} from './features/MiniBookingWidget/MiniBookingWidgetSlice';
import { openModal } from './features/modalSlice';
import { getAvailableFutureDates } from 'features/noAvailablePrices/NoAvailablePricesSlice';

import { ratestrip } from './interface';
import Offers from './components/offers/Offers';
import { BigBookingWidgetDrawer } from './components/bigBookingWidgetDrawer';
import { RetargetingOffers } from 'components/offers';
import { MobileMultipropertyDrawer } from 'components/mobileMultipropertyDrawer';
import { useOpenBigWidget, useInitCalendar, useLiveVisitors } from 'hooks';
import useSetCart from 'hooks/useSetCart';
import { RoundFloatingIcon } from 'components/roundFloatingIcon';
import { FaPhoneAlt } from 'react-icons/fa';

function App() {
    const [showWidget, setShowWidget] = useState(true);

    const widgetVariant = useSelector(
        (state) => state.miniBookingWidget?.data?.smallWidget
    );

    const propertyType = useSelector(
        (state) => state.miniBookingWidget?.data?.propertyType
    );

    const notificationsAvailable = useSelector(
        (state) => state.miniBookingWidget.notificationsAvailable
    );
    const notificationsUrl = useSelector(
        (state) => state.miniBookingWidget?.data?.notificationsUrl
    );

    const ourOffers =
        useSelector((state) => state.miniBookingWidget?.data?.ourOffers) || [];

    const retargetingOffers =
        useSelector((state) => state.miniBookingWidget?.preRetars) || [];
    const popUpNewOffers =
        useSelector((state) => state.miniBookingWidget?.popUpNewList) || [];
    const isOpen = useSelector((state) => state.miniBookingWidget.open);
    // Get reviews drawer anchor
    const widgetPosition = useSelector(
        (state) => state.miniBookingWidget?.widgetPosition
    );
    const drawerAnchor =
        widgetVariant === 'openBigDrawer'
            ? 'middle'
            : widgetPosition?.position?.split('-')[1] || 'right';
    const mobileRequest = isMobileDeviceRequest();
    let toastPosition =
        widgetPosition?.position?.split('-')[1] === 'right'
            ? 'bottom-left'
            : 'bottom-right';
    let tostZIndex = 1;
    if (mobileRequest) {
        toastPosition = 'top-left';
        tostZIndex = 'calc(var(--max-z-index) - 2)';
    }

    const getFutureDates = useSelector(
        (state) => state.miniBookingWidget?.data?.getFutureDates
    );
    const futureDatesUrl =
        useSelector((state) => state.miniBookingWidget?.data?.futureDatesUrl) ||
        '';

    const dispatch = useDispatch();

    const toggleWidget = () => {
        dispatch(toggle());
    };

    /*********************************************
     *
     * Public interface
     *
     *********************************************/

    ratestrip.minimize = () => {
        dispatch(minimize());
    };

    ratestrip.hide = () => {
        dispatch(hide());
    };

    ratestrip.show = (params = {}) => {
        let {
            adults,
            checkin,
            checkout,
            localeLanguage,
            currency,
            nights,
            rooms,
            children,
            infants,
            voucher,
            price,
            smartGuest,
            keepState,
            bookingEngine,
        } = params;

        let newPrice;
        if (price && typeof price === 'string') {
            price = parseFloat(price);
        }

        if (smartGuest && typeof smartGuest === 'string') {
            smartGuest = parseFloat(smartGuest);
        }

        if (smartGuest === 0) {
            newPrice = price;
        } else if (price === 0) {
            newPrice = smartGuest;
        } else {
            newPrice = [price, smartGuest][price < smartGuest ? 0 : 1];
        }

        if (bookingEngine === undefined) {
            bookingEngine = 1;
        }

        if (newPrice) {
            dispatch(setPrice({ price: newPrice }));
        }

        dispatch(
            getMiniBookingWidgetData({
                adults,
                checkin,
                checkout,
                localeLanguage,
                currency,
                nights,
                rooms,
                children,
                infants,
                voucher,
                price,
                smartGuest,
                bookingEngine,
                keepState,
            })
        );
    };

    ratestrip.showOffers = () => {
        if (ourOffers?.offersList?.length > 0) {
            dispatch(openModal({ modalId: 'OffersModal' }));
        }
    };

    /*********************************************
     *
     * Public interface END
     *
     *********************************************/
    // Notifications should be displayed only once
    const notificationsShown = useRef(false);

    useEffect(async () => {
        try {
            if (
                widgetVariant !== 'openBigDrawer' &&
                notificationsAvailable &&
                !notificationsShown.current
            ) {
                const response = await axios.get(notificationsUrl);

                if (response && response.data) {
                    const notifications = response.data.notifications || [];
                    displayNotifications(notifications);
                    dispatch(resetNotificationsAvailable());
                    notificationsShown.current = true;
                }
            }
        } catch (error) {}
    }, [notificationsAvailable]);

    // Get available future dates
    useEffect(() => {
        if (getFutureDates && futureDatesUrl) {
            dispatch(getAvailableFutureDates(futureDatesUrl));
        }
    }, [futureDatesUrl]);

    useEffect(() => {
        // Set isOpen in ls everytime when isOpen Changes

        // If 'isOpen' is not set to either 'true' or 'false' that means that
        // the initial request has not been made yet and 'isOpen' has its initial value.
        // So there is no need to save 'isOpen' to LS if its value is not set yet.
        if (isOpen !== null) {
            saveWidgetStateToLS({ isOpen });
        }
    }, [isOpen]);

    const intervalOptionIsDismissed = {
        intervalKey: 'isDismissed',
        intervalTimestampKey: 'lastIsDismissedTimestamp',
    };

    const intervalOptionIsOpen = {
        intervalKey: 'isOpen',
        intervalTimestampKey: 'lastMultipropertyCloseTimestamp',
    };

    // Load widget data
    useEffect(() => {
        if (
            widgetVariant === constants.MULTIPROPERTY ||
            widgetVariant === constants.SMALL_MULTIPROPERTY
        ) {
            checkWidgetIntervalsToDelete(intervalOptionIsOpen);
            checkWidgetIntervalsToDelete(intervalOptionIsDismissed);
        }
        dispatch(getMiniBookingWidgetData());
    }, []);

    useOpenBigWidget();
    useInitCalendar();
    useLiveVisitors();
    useSetCart();
    // Some type of custom routing
    const routes = {
        [constants.SMALL]: (
            <>
                <MobileBookingWidget showWidget={setShowWidget} />
                <MobileMultipropertyDrawer
                    anchor={drawerAnchor}
                    propertyType={propertyType}
                    widgetVariant={widgetVariant}
                />
            </>
        ),
        [constants.WIDGET_CLICK_2_CALL]: (
            <>
                <RoundFloatingIcon icon={<FaPhoneAlt />} hasTwoWidgets={true} />
                <DesktopBookingWidget
                    showWidget={setShowWidget}
                    toggleWidget={toggleWidget}
                />
            </>
        ),
        [constants.OPEN_SMALL_PLUGIN]: (
            <>
                <MobileBookingWidget showWidget={setShowWidget} />
                <MobileMultipropertyDrawer
                    anchor={drawerAnchor}
                    propertyType={propertyType}
                    widgetVariant={widgetVariant}
                />
            </>
        ),
        [constants.CLICK_2_CALL]: <RoundFloatingIcon icon={<FaPhoneAlt />} />,
        [constants.MULTIPROPERTY]: (
            <MultipropertyCarouselWidget
                showWidget={setShowWidget}
                toggleWidget={toggleWidget}
            />
            // to multipropertywidget to palio prin to carousel
            // <MultiPropertyBookingWidget
            //     showWidget={setShowWidget}
            //     toggleWidget={toggleWidget}
            // />
        ),
        [constants.MOBILE_MULTIPROPERTY]: (
            <>
                <MultipropertyCarouselWidget
                    showWidget={setShowWidget}
                    toggleWidget={toggleWidget}
                    mobile={true}
                    extraClass={'ab-mobile-carousel'}
                />

                <MobileMultipropertyDrawer
                    anchor={drawerAnchor}
                    propertyType={propertyType}
                    widgetVariant={widgetVariant}
                />
            </>
        ),
        [constants.REGULAR]: (
            <DesktopBookingWidget
                showWidget={setShowWidget}
                toggleWidget={toggleWidget}
            />
        ),
        [constants.HORIZONTAL]: (
            <DesktopHorizontalBookingWidget
                showWidget={setShowWidget}
                toggleWidget={toggleWidget}
            />
        ),
    };

    return (
        <>
            <div className="ab-booking-widget">
                {showWidget && routes[widgetVariant]}
            </div>
            {showWidget && <Offers />}
            <ToastContainer
                position={toastPosition}
                style={{ zIndex: tostZIndex }}
                closeOnClick={true}
            />

            {/* <ReviewsDrawer anchor={drawerAnchor} /> */}
            <BigBookingWidgetDrawer
                anchor={drawerAnchor}
                propertyType={propertyType}
                widgetVariant={widgetVariant}
            />

            <RetargetingOffers
                offers={
                    widgetVariant !== 'openBigDrawer' ? retargetingOffers : []
                }
            />

            <RetargetingOffers offers={popUpNewOffers} />
        </>
    );
}

export default App;
